import { Component, ElementRef, ViewChild, AfterViewChecked, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { Message } from '../models/chatbot.models';
import { ChatbotService } from '../../../services/chatbot.service';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslatorPipe } from '../../../core/translator/translator-pipe';

@Component({
  selector: 'app-chatbot-main',
  standalone: true,
  imports: [CommonModule, FormsModule, 
    MatInputModule, MatButtonModule, MatIconModule, 
    MatCardModule, MatDividerModule, TranslatorPipe],
  templateUrl: './chatbot-main.html',
  styleUrls: ['./chatbot-main.scss']
})
export class ChatbotMainComponent {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  public readonly chatbotService: ChatbotService = inject(ChatbotService);
  private sanitizer = inject(DomSanitizer);

  newMessage: string = '';
  showHistory = false;
  isLoading = false;
  currentItemDetails = signal<{ brand: string, model: string, serialNumber: string, type: 'motor' | 'equipo' } | null>(null);
  currentMotorRef = signal<string | null>(null);

  getMarkdown(content: string): SafeHtml {
    const html = marked.parse(content) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  constructor() {
    const pending = this.chatbotService.pendingQuestion();
    const pendingMotor = this.chatbotService.pendingMotorRef();
    const pendingDetails = this.chatbotService.pendingItemDetails();

    if (pending !== null) {
      this.chatbotService.pendingQuestion.set(null);
      this.chatbotService.pendingMotorRef.set(null);
      this.chatbotService.pendingItemDetails.set(null);
      this.currentItemDetails.set(pendingDetails);
      this.currentMotorRef.set(pendingMotor);
      this.chatbotService.createNewConversation();
      
      if (pendingDetails) {
        // If we have details but no specific initial message, we can send a default one or just show the title
        // In this case, the prompt from table was empty string
      }

      if (pending || pendingMotor || pendingDetails) {
        let messageContent = pending;
        
        // If there's a message to send (even if it's because we have details and want to trigger a prompt), send it.
        // BUT if messageContent is truly empty and we only want to set the "context" (motor_ref) 
        // without an initial greeting/question from the user, then we don't call sendMessage.
        
        if (messageContent) {
          const userMsg: Message = { role: 'user', content: messageContent, timestamp: new Date() };
          this.chatbotService.addMessage(userMsg);
          this.isLoading = true;
          this.scrollToBottom();
          this.chatbotService.sendMessage(userMsg.content, false, pendingMotor || undefined).subscribe({
            next: () => { this.isLoading = false; this.scrollToBottom(); },
            error: () => { this.isLoading = false; }
          });
        }
      }
      return;
    }

    this.chatbotService.loadUserThreads().subscribe({
      next: () => {
        const mostRecent = this.sortedConversations[0];
        if (mostRecent) {
          this.selectExistingConversation(mostRecent.id);
        } else {
          this.chatbotService.createNewConversation();
        }
      },
      error: () => this.chatbotService.createNewConversation()
    });
  }

  get messages() {
    return this.chatbotService.getCurrentMessages();
  }

  selectExistingConversation(id: string) {
    const alreadyLoaded = id === this.chatbotService.currentConversationId() &&
      (this.chatbotService.conversations().find(c => c.id === id)?.messages.length ?? 0) > 0;
    if (alreadyLoaded) return;
    this.currentItemDetails.set(null); // Clear item details when switching to history
    this.currentMotorRef.set(null);    // Clear ref when switching
    this.chatbotService.selectConversation(id);
    this.isLoading = true;
    this.chatbotService.loadChatHistory(id).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        // Check for motor_ref in multiple possible locations depending on API response structure
        const motorRef = res.motor_ref;
        
        if (motorRef) {
          this.currentMotorRef.set(motorRef);
          this.chatbotService.getItemDetails(motorRef).subscribe({
            next: (details) => {
              this.currentItemDetails.set(details);
            }
          });
        }
        this.scrollToBottom();
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  get sortedConversations() {
    return [...this.chatbotService.conversations()].sort((a, b) => {
      const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
      const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
      return dateB - dateA;
    });
  }

  get lastMessage() {
    const msgs = this.messages;
    return msgs.length > 0 ? msgs[msgs.length - 1] : null;
  }

  toggleHistory() {
    this.showHistory = !this.showHistory;
  }

  createNewConversation() {
    this.currentItemDetails.set(null);
    this.currentMotorRef.set(null);
    this.chatbotService.createNewConversation();
  }

  sendMessage() {
    if (this.newMessage.trim() && !this.isLoading) {
      const userMsg: Message = {
        role: 'user',
        content: this.newMessage.trim(),
        timestamp: new Date()
      };
      
      this.chatbotService.addMessage(userMsg);
      const textToSend = this.newMessage;
      this.newMessage = '';
      this.isLoading = true;
      this.scrollToBottom();

      this.chatbotService.sendMessage(textToSend, false, this.currentMotorRef() || undefined).subscribe({
        next: () => {
          this.isLoading = false;
          this.scrollToBottom();
        },
        error: (err) => {
          console.error('Error sending message', err);
          this.isLoading = false;
        }
      });
    }
  }

  respondInterrupt(answer: string) {
    if (!this.isLoading) {
      const userMsg: Message = {
        role: 'user',
        content: answer,
        timestamp: new Date()
      };
      
      this.chatbotService.addMessage(userMsg);
      this.isLoading = true;
      this.scrollToBottom();

      this.chatbotService.sendMessage(answer, true, this.currentMotorRef() || undefined).subscribe({
        next: () => {
          this.isLoading = false;
          this.scrollToBottom();
        },
        error: (err) => {
          console.error('Error responding to interrupt', err);
          this.isLoading = false;
        }
      });
    }
  }

  deleteConversation(event: MouseEvent, id: string) {
    event.stopPropagation();
    this.chatbotService.deleteConversation(id).subscribe({
      next: () => {
        // Si no quedan conversaciones, crear una nueva
        if (this.chatbotService.conversations().length === 0) {
          this.chatbotService.createNewConversation();
        } else if (this.chatbotService.currentConversationId() === null) {
          // Si eliminamos la actual, seleccionar la primera disponible
          const nextConv = this.chatbotService.conversations()[0];
          this.selectExistingConversation(nextConv.id);
        }
      },
      error: (err) => {
        console.error('Error deleting conversation', err);
      }
    });
  }

  private scrollToBottom(): void {
    if (this.scrollContainer) {
      setTimeout(() => {
        try {
          this.scrollContainer.nativeElement.scrollTo({
            top: this.scrollContainer.nativeElement.scrollHeight,
            behavior: 'smooth'
          });
        } catch (err) { }
      }, 100);
    }
  }
}
